/**
 * PhoneInput — reusable phone field with country flag + dial code prefix.
 *
 * Default dial code resolves from: defaultCountryCode prop → active company → 'NG'.
 * Users can change the prefix via a compact dropdown menu.
 */
import { useState, useRef, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  ButtonBase,
  Menu,
  MenuItem,
  Typography,
  Divider,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { COUNTRIES, getCountryByCode } from '@/components/common/CountrySelect';
import { useAuthStore } from '@/stores/authStore';

/* Sort dial codes longest-first for unambiguous prefix matching */
const SORTED_COUNTRIES = [...COUNTRIES].sort(
  (a, b) => b.dialCode.length - a.dialCode.length,
);

/** Parse a stored phone value like "+234 8001234567" into { countryCode, localNumber }. */
function parsePhone(
  value: string,
  fallbackCountryCode: string,
): { countryCode: string; localNumber: string } {
  const trimmed = (value ?? '').trim();
  if (trimmed.startsWith('+')) {
    for (const c of SORTED_COUNTRIES) {
      if (trimmed.startsWith(c.dialCode)) {
        return {
          countryCode: c.code,
          localNumber: trimmed.slice(c.dialCode.length).trim(),
        };
      }
    }
  }
  // No dial code prefix detected — treat entire value as local number
  return { countryCode: fallbackCountryCode, localNumber: trimmed };
}

interface PhoneInputProps {
  /** Full phone value including dial code, e.g. "+234 8001234567" */
  value: string;
  /** Called with the full phone string including dial code */
  onChange: (value: string) => void;
  /** ISO country code for default flag/dial code (overrides active company) */
  defaultCountryCode?: string;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  required?: boolean;
  sx?: SxProps<Theme>;
}

export default function PhoneInput({
  value,
  onChange,
  defaultCountryCode,
  label,
  placeholder = '800 000 0000',
  error,
  helperText,
  fullWidth = true,
  size = 'small',
  disabled = false,
  required = false,
  sx,
}: PhoneInputProps) {
  const activeCompany = useAuthStore((s) => s.getActiveCompany());
  const resolvedDefault = defaultCountryCode || activeCompany?.country_code || 'NG';

  const parsed = useMemo(() => parsePhone(value, resolvedDefault), [value, resolvedDefault]);

  const [selectedCode, setSelectedCode] = useState(parsed.countryCode);
  const [localNumber, setLocalNumber] = useState(parsed.localNumber);
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  // Sync when external value changes (e.g. form reset, data load)
  useEffect(() => {
    const p = parsePhone(value, resolvedDefault);
    setSelectedCode(p.countryCode);
    setLocalNumber(p.localNumber);
  }, [value, resolvedDefault]);

  const country = getCountryByCode(selectedCode) ?? getCountryByCode('NG')!;

  const emit = (dialCode: string, num: string) => {
    const cleaned = num.trim().replace(/[\s()-]/g, '');
    const full = cleaned ? `${dialCode}${cleaned}` : '';
    onChange(full);
  };

  const handleNumberChange = (num: string) => {
    setLocalNumber(num);
    emit(country.dialCode, num);
  };

  const handleCountrySelect = (code: string) => {
    setSelectedCode(code);
    setMenuOpen(false);
    const c = getCountryByCode(code);
    if (c) emit(c.dialCode, localNumber);
  };

  return (
    <>
      <TextField
        value={localNumber}
        onChange={(e) => handleNumberChange(e.target.value)}
        label={label}
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
        size={size}
        disabled={disabled}
        required={required}
        sx={sx}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <ButtonBase
                  ref={anchorRef}
                  onClick={() => !disabled && setMenuOpen(true)}
                  disabled={disabled}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    borderRadius: 1,
                    px: 0.5,
                    py: 0.25,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                      lineHeight: 1,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 22,
                        flexShrink: 0,
                        fontSize: '1.05em',
                        lineHeight: 1,
                      }}
                    >
                      {country.flag}
                    </Box>
                    {country.dialCode}
                    <ArrowDropDown sx={{ fontSize: 18, color: 'text.secondary', ml: -0.25 }} />
                  </Typography>
                </ButtonBase>
                <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 22, alignSelf: 'center' }} />
              </InputAdornment>
            ),
          },
        }}
      />

      <Menu
        anchorEl={anchorRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        slotProps={{ paper: { sx: { minWidth: 220, maxHeight: 300 } } }}
      >
        {COUNTRIES.map((c) => (
          <MenuItem
            key={c.code}
            selected={c.code === selectedCode}
            onClick={() => handleCountrySelect(c.code)}
            sx={{ gap: 1.5 }}
          >
            <Typography sx={{ fontSize: '1.2em', lineHeight: 1 }}>{c.flag}</Typography>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2">{c.name}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">{c.dialCode}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
